'use client';

import { FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { classicAppUrl, getClassicAppDisplayHost } from '@/lib/classic-app';

interface TermsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Shared Terms-of-Use dialog. The standalone `/terms` page was retired
 * in favour of this in-app modal so the footer (lobby + classic) and
 * any other surface that needs to surface the terms can pop the same
 * authoritative copy without a route change.
 *
 * Copy is duplicated from the previous `/terms` page; if it changes,
 * update here once.
 */
export function TermsDialog({ open, onOpenChange }: TermsDialogProps) {
  const classicDisplayHost = getClassicAppDisplayHost();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#0b0a08] border-amber-500/30 text-zinc-100">
        <DialogHeader>
          <DialogTitle className="text-amber-100 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Terms of Use
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-[12px]">
            Last updated · April 29, 2026
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-[12px] text-zinc-300 max-h-[60vh] overflow-y-auto pr-2">
          <section className="space-y-1.5">
            <h3 className="text-amber-100 font-semibold text-[13px]">The Short Version</h3>
            <p>
              takeover.fun is an experimental protocol. You can lose money.
              We can&apos;t help you get it back. Use at your own risk.
            </p>
          </section>

          <section className="space-y-1.5">
            <h3 className="text-amber-100 font-semibold text-[13px]">1. What This Is</h3>
            <p>
              takeover.fun is experimental software for interacting with smart
              contracts on Base. It&apos;s not a bank, broker, or financial
              advisor. We don&apos;t hold your funds or make investment
              decisions for you.
            </p>
          </section>

          <section className="space-y-1.5">
            <h3 className="text-amber-100 font-semibold text-[13px]">2. The Risks</h3>
            <p>This is an experiment. Things that can happen:</p>
            <ul className="list-disc list-inside space-y-0.5 ml-2">
              <li>Smart contracts may have bugs</li>
              <li>You can lose some or all of your funds</li>
              <li>Someone can buy your tile at any time</li>
              <li>Fees can drain your balance to zero</li>
              <li>Blockchain transactions are irreversible</li>
              <li>AI agents may outcompete human players</li>
            </ul>
            <p>We make no guarantees about anything.</p>
          </section>

          <section className="space-y-1.5">
            <h3 className="text-amber-100 font-semibold text-[13px]">3. Your Responsibilities</h3>
            <ul className="list-disc list-inside space-y-0.5 ml-2">
              <li>Keep your wallet secure</li>
              <li>Understand what you&apos;re doing before you do it</li>
              <li>Follow the laws in your jurisdiction</li>
              <li>Handle your own taxes</li>
              <li>Monitor your positions</li>
            </ul>
          </section>

          <section className="space-y-1.5">
            <h3 className="text-amber-100 font-semibold text-[13px]">4. Not Investment Advice</h3>
            <p>
              Nothing here is investment, legal, or tax advice. Do your own
              research. Consult professionals if needed.
            </p>
          </section>

          <section className="space-y-1.5">
            <h3 className="text-amber-100 font-semibold text-[13px]">
              5. Data Accuracy &amp; Limitations
            </h3>
            <p>
              All data displayed on takeover.fun — including prices, earnings,
              rewards, balances, and other metrics — is strictly historical
              and provided for informational purposes only. It is not
              predictive, forward-looking, or indicative of future results.
              You acknowledge and agree that:
            </p>
            <ul className="list-disc list-inside space-y-0.5 ml-2">
              <li>
                Displayed values may be inaccurate, delayed, or incomplete
                due to liquidity conditions, exchange rate fluctuations,
                indexer lag, or other factors
              </li>
              <li>
                USD-equivalent values are estimates derived from onchain data
                and third-party price feeds, which can be imprecise
              </li>
              <li>
                Past performance, historical earnings, and prior reward
                distributions do not guarantee or imply any future returns
              </li>
              <li>
                You must independently verify all data before taking any
                action, including but not limited to claiming, depositing,
                withdrawing, or setting prices
              </li>
              <li>
                We are not responsible for any losses resulting from reliance
                on data displayed in the interface
              </li>
            </ul>
          </section>

          <section className="space-y-1.5">
            <h3 className="text-amber-100 font-semibold text-[13px]">
              6. Limitation of Liability
            </h3>
            <p>
              To the maximum extent permitted by law, we&apos;re not liable
              for any losses you incur using this protocol. The smart
              contracts run autonomously — we can&apos;t reverse transactions
              or recover funds.
            </p>
          </section>

          <section className="space-y-1.5">
            <h3 className="text-amber-100 font-semibold text-[13px]">7. Points &amp; Rewards</h3>
            <p>
              takeover.fun may award &quot;points&quot; for various activities
              such as signing up, linking social accounts, sharing content,
              or referring other users. By participating, you acknowledge and
              agree that:
            </p>
            <ul className="list-disc list-inside space-y-0.5 ml-2">
              <li>Points have no monetary or material value whatsoever</li>
              <li>
                Points do not represent any form of currency, security,
                commodity, or financial instrument
              </li>
              <li>
                Points are not redeemable for cash, cryptocurrency, or any
                other asset
              </li>
              <li>
                Points do not constitute a guarantee, promise, or obligation
                of any future reward, airdrop, token distribution, or benefit
                of any kind
              </li>
              <li>
                The point system, including point balances, accrual rates,
                and eligibility criteria, may be modified, reset, or
                discontinued at any time at our sole discretion without
                prior notice
              </li>
              <li>
                We reserve the right to revoke, adjust, or nullify any or
                all points at any time for any reason
              </li>
              <li>
                No rights, claims, or expectations of any kind arise from
                the accumulation of points
              </li>
              <li>
                Any future decisions regarding the utility or conversion of
                points, if any, will be made entirely at our discretion and
                are not guaranteed
              </li>
            </ul>
            <p>
              Participation in the points program is entirely voluntary and
              at your own risk. You should not acquire points with the
              expectation of receiving any compensation or reward.
            </p>
          </section>

          <section className="space-y-1.5">
            <h3 className="text-amber-100 font-semibold text-[13px]">8. Notifications</h3>
            <p>
              takeover.fun may provide optional notifications including, but
              not limited to, tile buyout alerts, deposit expiry warnings,
              and chat mention alerts via in-app, browser push, or other
              channels. <strong className="text-amber-100">Push
              notifications are currently in beta and should not be relied
              on fully — always check the status of your land directly in
              the app.</strong> By using the notification features, you
              acknowledge and agree that:
            </p>
            <ul className="list-disc list-inside space-y-0.5 ml-2">
              <li>
                Notifications are provided on a best-effort basis and may be
                delayed, lost, or fail to deliver without notice
              </li>
              <li>
                You should not rely on notifications as your sole means of
                monitoring seat deposits, buyouts, or other time-sensitive
                onchain activity
              </li>
              <li>
                We are not responsible for any losses, forfeitures, or missed
                opportunities resulting from notifications that are not
                delivered, are delivered late, or contain inaccurate
                information
              </li>
              <li>
                Notification availability, frequency, and delivery methods
                may change at any time without prior notice
              </li>
              <li>
                It is your responsibility to independently monitor your
                positions and onchain state
              </li>
            </ul>
          </section>

          <section className="space-y-1.5">
            <h3 className="text-amber-100 font-semibold text-[13px]">
              9. Tile Deposits &amp; Forfeiture
            </h3>
            <p>
              Each tile you buy or claim is held under a Harberger-style
              deposit / continuous-tax model. <strong className="text-amber-100">
              You must keep your deposit topped up to cover tax. If it
              runs out, your land gets forfeit to another buyer.</strong>
              {' '}By buying, claiming, or minting a tile you acknowledge
              and agree that:
            </p>
            <ul className="list-disc list-inside space-y-0.5 ml-2">
              <li>
                Tax accrues continuously based on the self-assessed price
                you set on each tile, and is drawn from the deposit attached
                to that tile
              </li>
              <li>
                If the deposit reaches zero, the tile becomes eligible to
                be forfeited and may be claimed by any other buyer without
                further notice or compensation
              </li>
              <li>
                It is your responsibility — not ours — to monitor each
                tile&apos;s deposit, top up before runway expires, and
                adjust the price if you can no longer cover the tax
              </li>
              <li>
                Notifications, runway estimates, and other UI cues are
                informational only and may be delayed, inaccurate, or
                missing entirely (see Section 8)
              </li>
              <li>
                Forfeitures are processed onchain and are irreversible;
                we cannot recover, refund, or restore a forfeited tile
              </li>
            </ul>
          </section>

          <section className="space-y-1.5">
            <h3 className="text-amber-100 font-semibold text-[13px]">10. Changes</h3>
            <p>
              We may update these terms. Continued use means you accept
              updates.
            </p>
          </section>

          <section className="space-y-1.5">
            <h3 className="text-amber-100 font-semibold text-[13px]">11. No Warranties</h3>
            <p>
              The protocol is provided &quot;as is&quot; without any
              warranties. We don&apos;t guarantee it will work, be available,
              or be secure.
            </p>
          </section>

          <section className="space-y-1.5 border-t border-amber-500/15 pt-3">
            <h3 className="text-amber-100 font-semibold text-[13px]">
              The Even Shorter Version
            </h3>
            <p className="font-medium">
              Experimental software. Not financial advice. You can lose
              everything. Your responsibility.
            </p>
          </section>

          <p className="text-[11px] italic text-zinc-400">
            Questions? Reach out on{' '}
            <a
              href="https://twitter.com/takeoverfun"
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-300 underline underline-offset-2 hover:text-amber-200"
            >
              Twitter/X
            </a>
            . The classic app remains available at{' '}
            <a
              href={classicAppUrl('/')}
              target="_blank"
              rel="noreferrer"
              className="text-amber-300 underline underline-offset-2 hover:text-amber-200"
            >
              {classicDisplayHost}
            </a>
            .
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
